"use client";

import CustomImage from '@/components/image';
import styles from './_majority.module.scss';
import PageAnchor from '@/components/page-anchor';

export default function HcpClinicalInformationMajority() {
  return (
    <>
      <PageAnchor anchorId="trial-2-efficacy-majority" />
      <section className={styles.container}>
        <div className="container">
          <h2>Majority of adults taking CRENESSITY achieved and maintained a GC dose in the physiologic range for up to 2 years<sup>1,7,10</sup></h2>

          <div className={styles.content}>
            <p className={styles.subtitle}><strong>Achieved daily GC dose in the physiologic range</strong><sup>1,7,10</sup><span className="super-asterisc">*</span><sup>&dagger;</sup></p>

            <div className={styles.graphs}>
              <div className={styles.graphs__secondary}>
                <div className={styles.pill}>While androstenedione levels were maintained or improved<sup className={styles.pill__symbol}>&Dagger;</sup></div>
                <picture>
                  <source
                    media="(max-width: 767px)"
                    srcSet="/images/page/hcp/clinical-information/Clinical-Information_Adult-Physiologic-Daily-GC-Dose_mobile.svg"
                  />
                  <CustomImage
                    src="/images/page/hcp/clinical-information/Clinical-Information_Adult-Physiologic-Daily-GC-Dose.svg"
                    width={701}
                    height={448}
                    priority
                    alt="63% of patients taking CRENESSITY achieved a daily GC dose in the physiologic range vs 18% with placebo."
                  />
                </picture>
              </div>
              <picture className={styles.graphs__exploratory}>
                <source
                  media="(max-width: 767px)"
                  srcSet="/images/page/hcp/clinical-information/Clinical-Information_Adult-Physiologic-Daily-GC-Dose-Exploratory_mobile.svg"
                />
                <CustomImage
                  src="/images/page/hcp/clinical-information/Clinical-Information_Adult-Physiologic-Daily-GC-Dose-Exploratory.svg"
                  width={424}
                  height={449}
                  alt="Exploratory endpoint at 2 years: 69% of adults taking combined CRENESSITY achieved a daily GC dose in the physiologic range while androstenedione levels were maintained or improved."
                />
              </picture>
            </div>

            <ul className={`footnotes ${styles.footnotes}`}>
              <li><span className="hang">*</span><span>In the clinical trial, the physiologic range was defined as &le;20 mg/day in hydrocortisone equivalents.<sup>1,8</sup></span></li>
              <li><sup className="hang">&dagger;</sup><span>Mean baseline GC doses in hydrocortisone equivalents were 32.3 &plusmn; 9.3 mg/day for all participants, 32.1 &plusmn; 9.5 mg/day for placebo, and 32.4 &plusmn; 9.2 mg/day for CRENESSITY.<sup>10</sup></span></li>
              <li><sup className="hang">&Dagger;</sup><span>Maintaining or improving androstenedione level was defined as &le;120% of the baseline value or &le;ULN for age and sex. If a patient&apos;s androstenedione level was not maintained or improved with a GC dose reduction, the % change from baseline for daily GC dose was recorded as 0.<sup>1,10,25</sup></span></li>
              <li><sup className="hang">&sect;</sup><span>The 2 year data for GC reduction were determined without protocol-defined reference to androstenedione level at the visit.<sup>7</sup></span></li>
              <li><sup className="hang">&para;</sup><span>The 2-year timepoint included patients treated with CRENESSITY for 24 months and patients treated with CRENESSITY for 18 months.<sup>7</sup></span></li>
            </ul>

            <div className={`hcp-callout ${styles.callout}`}>
              The percentage of adults on CRENESSITY <strong>for up to 2 years</strong> who achieved a physiologic GC dose generally remained stable.<sup>7</sup>
            </div>

            <p className={styles.chance_finding}>
              <sup className={styles.chance_finding__symbol}>#</sup>
              <span>The prespecified exploratory endpoint was not adjusted for multiplicity; this descriptive result could represent chance findings.<sup className="!font-normal">27</sup></span>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}